import {
  AutomationTestJobStatus,
  DeploymentRequestDto,
  DeploymentStatus,
} from "@/api/models";
import { formatRelative, intervalToDuration } from "date-fns";

export const MAX_BRANCH_IDENTIFIER_LENGTH = 49;

export function sanitizeBranchIdentifier(branchName: string): string {
  // Branches and tags share the same resource/conflict namespace.
  let identifier = branchName.replace(/^refs\/(heads|tags)\//, "");
  if (branchName.startsWith("refs/tags/")) {
    identifier = identifier.split("/").pop() ?? "";
  }

  // Replace # with empty, . and spaces with -
  identifier = identifier.replace(/#/g, "").replace(/\./g, "-").replace(/ /g, "-");
  return identifier;
}

export function isBranchNameTooLong(branchName: string): boolean {
  const identifier = sanitizeBranchIdentifier(branchName);
  return identifier.length > MAX_BRANCH_IDENTIFIER_LENGTH;
}

export function formatBranchName(branchName: string): string {
  return branchName.replace("refs/heads/", "");
}

export function formatDate(date: string): string {
  return formatRelative(new Date(date), new Date());
}
export function getAge(deploymentRequest: DeploymentRequestDto): string {
  return getDuration(new Date(deploymentRequest.deployedDate), new Date());
}

export function getLastStarted(deploymentRequest: DeploymentRequestDto): string {
  const lastStartedDate = deploymentRequest.lastStartedDate ?? deploymentRequest.deployedDate;
  return lastStartedDate ? getDuration(new Date(lastStartedDate), new Date()) : "";
}

export function getAgeInDays(deploymentRequest: DeploymentRequestDto): number {
  const deployedDate = new Date(deploymentRequest.deployedDate);
  const now = new Date();
  const differenceInMs = now.getTime() - deployedDate.getTime();
  const differenceInDays = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));
  return differenceInDays;
}

export function getDuration(start: Date, end: Date): string {
  const result = intervalToDuration({
    start: start,
    end: end,
  });
  let age = "";
  if (result.years > 0) {
    age += `${result.years}yrs`;
  }

  if (result.months > 0) {
    age += ` ${result.months}mos`;
  }

  if (result.days > 0) {
    age += ` ${result.days}d`;
  }

  if (result.hours > 0) {
    age += ` ${result.hours}h`;
  }

  if (result.minutes > 0) {
    age += ` ${result.minutes}m`;
  }

  if (result.seconds > 0) {
    age += ` ${result.seconds}s`;
  }

  return age;
}

export function formatStatus(
  num: number,
  enumType: Record<number, string>
): string {
  return enumType[num].split(/(?=[A-Z])/).join(" ");
}

export function formatDeploymentStatus(status: DeploymentStatus): string {
  return formatStatus(status, DeploymentStatus);
}
export function formatAutomationTestJobStatus(
  status: AutomationTestJobStatus
): string {
  return formatStatus(status, AutomationTestJobStatus);
}

export function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

export function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function highlightText(text: string, searchQuery: string): string {
  if (!searchQuery) return escapeHtml(text);
  const escapedText = escapeHtml(text);
  const escapedQuery = escapeRegExp(searchQuery);
  const highlightedText = escapedText.replace(
    new RegExp(escapedQuery, "gi"),
    (match) => `<mark class="highlight">${match}</mark>`
  );
  return highlightedText;
}
export interface E2ETestConfig {
  priority: { P0: boolean; P1: boolean; P2: boolean };
  selective: boolean;
  excludeFlaky: boolean;
  repeat: number;
  testFile: string;
  extraFlags: string;
}

export function buildTestTag(cfg: E2ETestConfig): string {
  const parts: string[] = [];
  const prio = (['P0', 'P1', 'P2'] as const).filter(p => cfg.priority[p]);
  if (prio.length && prio.length < 3) {
    parts.push('--grep', '@' + prio.join('|@'));
  }
  if (cfg.excludeFlaky) parts.push('--grep-invert', '@Flaky');
  if (cfg.selective) parts.push('--selective');
  if (cfg.repeat > 1) parts.push(`--repeat-each=${cfg.repeat}`);
  if (cfg.testFile.trim()) parts.push(cfg.testFile.trim());
  if (cfg.extraFlags.trim()) parts.push(cfg.extraFlags.trim());
  return parts.join(' ');
}

export function buildE2EPreviewCommand(cfg: E2ETestConfig): string {
  const tag = buildTestTag(cfg);
  return tag ? `pnpm exec playwright test ${tag}` : 'pnpm exec playwright test';
}

let cachedBaseUrl: string | null = null;
export function getApiBaseUrl(): string {
  if (cachedBaseUrl) {
    return cachedBaseUrl;
  }
  const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
  cachedBaseUrl = envBaseUrl?.endsWith("/") ? envBaseUrl : envBaseUrl + "/";
  return cachedBaseUrl;
}
